import { useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, ExternalLink, Headphones, Loader2, Square } from "lucide-react";

/**
 * Konsolda yayın dinleme: sayfa içinde tek bir <audio>. Yeni sekmede açmak
 * "audio/aacp" gibi türlerde dosya indiriyordu; <audio> bunları çalar. Aynı
 * anda tek yayın çalar, başka birine basınca öncekisi durur. HLS (.m3u8)
 * tarayıcı desteklemiyorsa hls.js yalnızca gerektiğinde yüklenir.
 */

type Phase = "idle" | "loading" | "playing" | "error";

interface PlayerState {
  url: string | null;
  phase: Phase;
  error: string | null;
}

let state: PlayerState = { url: null, phase: "idle", error: null };
const listeners = new Set<() => void>();
const set = (next: Partial<PlayerState>): void => {
  state = { ...state, ...next };
  for (const l of listeners) l();
};

let audio: HTMLAudioElement | null = null;
let hls: { destroy: () => void } | null = null;

function teardown(): void {
  hls?.destroy();
  hls = null;
  if (audio) {
    audio.pause();
    audio.removeAttribute("src");
    audio.load();
  }
}

/** Tarayıcının verdiği hatayı insan diline çevirir; güvenli sayfada http yayını ayrıca söylenir. */
function explain(url: string, code: number | undefined): string {
  if (url.startsWith("http:") && location.protocol === "https:") {
    return "http yayını güvenli sayfada çalınamadı; yeni sekmede aç";
  }
  if (code === 4) return "tarayıcı bu yayını açamadı (biçim desteklenmiyor ya da adres yanıt vermiyor)";
  if (code === 2) return "ağ hatası: yayın sunucusuna ulaşılamadı";
  return "yayın açılamadı";
}

function stop(): void {
  teardown();
  set({ url: null, phase: "idle", error: null });
}

async function play(url: string): Promise<void> {
  teardown();
  set({ url, phase: "loading", error: null });
  if (!audio) {
    audio = new Audio();
    audio.preload = "none";
    audio.addEventListener("playing", () => set({ phase: "playing", error: null }));
    audio.addEventListener("waiting", () => state.phase === "playing" && set({ phase: "loading" }));
    audio.addEventListener("error", () => {
      if (!state.url) return;
      set({ phase: "error", error: explain(state.url, audio?.error?.code) });
    });
  }
  const el = audio;
  try {
    const isHls = /\.m3u8(\?|$)/i.test(new URL(url).pathname);
    if (isHls && !el.canPlayType("application/vnd.apple.mpegurl")) {
      const { default: Hls } = await import("hls.js");
      if (state.url !== url) return;
      if (!Hls.isSupported()) throw new Error("tarayıcı HLS desteklemiyor");
      const h = new Hls();
      hls = h;
      h.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal && state.url === url) set({ phase: "error", error: `HLS hatası: ${data.details}` });
      });
      h.loadSource(url);
      h.attachMedia(el);
    } else {
      el.src = url;
    }
    await el.play();
  } catch (err) {
    if (state.url !== url) return;
    // play() reddi çoğunlukla "error" olayıyla zaten açıklanmıştır; HLS desteği yoksa mesaj buradan.
    if (state.phase !== "error") {
      set({ phase: "error", error: audio?.error ? explain(url, audio.error.code) : (err as Error).message || explain(url, undefined) });
    }
  }
}

const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/**
 * Bir yayın için Dinle/Durdur düğmesi; hata olursa açıklama ve yeni sekmede
 * açma bağlantısı. Etiket dar ekranda gizlenir (compact).
 */
export function ListenButton({ url, name, compact = false }: { url: string; name: string; compact?: boolean }): ReactNode {
  const s = useSyncExternalStore(subscribe, () => state);
  const mine = s.url === url;
  const phase: Phase = mine ? s.phase : "idle";
  const active = phase === "loading" || phase === "playing";

  return (
    <span className="inline-flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`btn btn-sm ${active ? "btn-secondary" : "btn-ghost"}`}
        aria-pressed={active}
        aria-label={active ? `${name} durdur` : `${name} dinle`}
        title={active ? "Durdur" : phase === "error" ? `${s.error ?? "Yayın açılamadı"} · yeniden dene` : `Dinle: ${url}`}
        onClick={(e) => {
          e.preventDefault();
          if (active) stop();
          else void play(url);
        }}
      >
        {phase === "loading" ? (
          <Loader2 size={14} className="spin" aria-hidden />
        ) : phase === "playing" ? (
          <Square size={12} fill="currentColor" aria-hidden />
        ) : phase === "error" ? (
          <AlertTriangle size={14} className="text-warn" aria-hidden />
        ) : (
          <Headphones size={14} strokeWidth={1.9} aria-hidden />
        )}
        <span className={compact ? "hidden sm:inline" : ""}>
          {phase === "loading" ? "Açılıyor" : phase === "playing" ? "Durdur" : phase === "error" ? "Açılamadı" : "Dinle"}
        </span>
      </button>
      {phase === "error" && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="grid size-7 place-items-center rounded-[var(--radius-sm)] text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
          title={`${s.error ?? "Yayın açılamadı"} · yeni sekmede aç`}
          aria-label={`${name}: ${s.error ?? "yayın açılamadı"}; yeni sekmede aç`}
        >
          <ExternalLink size={13} aria-hidden />
        </a>
      )}
    </span>
  );
}
